import { useEffect } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useAuth } from '@/contexts/AuthContext'
import { trpc } from '@/lib/trpc'

/** Every per-account flag, with its default and the copy the settings screen shows. */
export const FEATURE_FLAGS = {
  useCropMask: {
    default: false,
    label: 'Focus mask',
    description:
      'Dim everything except the selected annotation so the cell you are reviewing stands out.',
  },
  cornerBracketHandles: {
    default: false,
    label: 'Corner bracket handles',
    description: 'Resize handles as L-shaped brackets on the corners of the box, instead of dots.',
  },
  showMeasurementRuler: {
    default: false,
    label: 'Measurement ruler',
    description: 'Show the width and height of the selected annotation, in image pixels.',
  },
} as const

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS

const DEFAULT_FLAGS = Object.fromEntries(
  Object.entries(FEATURE_FLAGS).map(([k, v]) => [k, v.default])
) as Record<FeatureFlagKey, boolean>

interface FeatureFlagStore {
  flags: Record<FeatureFlagKey, boolean>
  /** Which account the flags were last loaded for; '' until the first sync */
  accountId: string
  setFlag: (key: FeatureFlagKey, value: boolean) => void
  replaceFlags: (accountId: string, flags: Partial<Record<string, boolean>>) => void
}

/** Flags live on the account (users.feature_flags, via trpc `user.me`); this store
 *  is the in-memory copy plus a localStorage cache so a reload doesn't flash defaults. */
export const useFeatureFlagStore = create<FeatureFlagStore>()(
  persist(
    (set) => ({
      flags: { ...DEFAULT_FLAGS },
      accountId: '',
      setFlag: (key, value) => set((s) => ({ flags: { ...s.flags, [key]: value } })),
      replaceFlags: (accountId, flags) =>
        set(() => ({
          accountId,
          flags: Object.fromEntries(
            (Object.keys(DEFAULT_FLAGS) as FeatureFlagKey[]).map((k) => [
              k,
              typeof flags[k] === 'boolean' ? flags[k] : DEFAULT_FLAGS[k],
            ])
          ) as Record<FeatureFlagKey, boolean>,
        })),
    }),
    { name: 'feature-flags-storage', version: 2 }
  )
)

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useFeatureFlagStore((s) => s.flags[key])
}

/** Mount once inside the authenticated tree: pulls the account's flags after
 *  login (and on account switch) and resets to defaults on logout. */
export function useSyncFeatureFlags() {
  const { user, isAuthenticated } = useAuth()
  const replaceFlags = useFeatureFlagStore((s) => s.replaceFlags)
  const me = trpc.user.me.useQuery(undefined, { enabled: isAuthenticated, staleTime: Infinity })

  useEffect(() => {
    if (!isAuthenticated) {
      replaceFlags('', {})
      return
    }
    if (me.data && user) replaceFlags(user.id, me.data.featureFlags)
  }, [isAuthenticated, me.data, user, replaceFlags])
}

/** Optimistic toggle that persists to the account. Rolls back if the save fails. */
export function useSetFeatureFlag() {
  const setFlag = useFeatureFlagStore((s) => s.setFlag)
  const utils = trpc.useUtils()
  const mutation = trpc.user.setFeatureFlags.useMutation({
    onSuccess: (flags) => {
      utils.user.me.setData(undefined, (prev) => (prev ? { ...prev, featureFlags: flags } : prev))
    },
  })
  return {
    saving: mutation.isPending,
    error: mutation.error?.message ?? null,
    set: (key: FeatureFlagKey, value: boolean) => {
      setFlag(key, value)
      mutation.mutate({ flags: { [key]: value } }, { onError: () => setFlag(key, !value) })
    },
  }
}
